import styled from 'styled-components';

/* ── Outer wrapper ───────────────────────────────────────────────────── */

/**
 * Sticky header wrapper. Stays at the top of the viewport on desktop;
 * renders inline on mobile until scrolled past, then fixes at top via
 * MobileFixedWrapper.
 */
export const StickyWrapper = styled.div`
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 999;
  background: #ffffff;
  border-bottom: 1px solid #D8DADA;

  @media (max-width: 767px) {
    position: static;
  }
`;

/**
 * Fixed-position fallback rendered on mobile once the user scrolls past
 * the inline progress bar position.
 */
export const MobileFixedWrapper = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 999;
    background: #ffffff;
    border-bottom: 1px solid #D8DADA;
  }
`;

/**
 * Invisible spacer injected below the inline bar on mobile to prevent
 * content jump when the bar switches to fixed positioning.
 */
export const MobileSpacer = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
    height: ${({ height }) => `${height}px`};
  }
`;

/* ── Inner layout ────────────────────────────────────────────────────── */

/**
 * Horizontal flex container that houses the stepper and the cart summary
 * side-by-side within a centred max-width column.
 *
 * On mobile: stacks vertically with breadcrumb navigation on top row and
 * pricing summary below.
 */
export const BarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1272px;
  margin: 0 auto;
  padding: 16px 24px;
  box-sizing: border-box;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: ${({ $isStepperSuppressed }) => ($isStepperSuppressed ? 'row' : 'column')};
    align-items: ${({ $isStepperSuppressed }) => ($isStepperSuppressed ? 'center' : 'flex-start')};
    justify-content: ${({ $isStepperSuppressed }) => ($isStepperSuppressed ? 'space-between' : 'flex-start')};
    padding: 8px 16px;
    gap: ${({ $isStepperSuppressed }) => ($isStepperSuppressed ? '8px' : '0')};
  }
`;

/* ── Stepper ─────────────────────────────────────────────────────────── */
// StepperWrapper, DesktopStepperContainer, MobileStepperWrapper,
// StepConnector, StepNode, StepCircle, StepLabel, BreadcrumbChevron
// are now co-located with the Stepper sub-component:
//   ./Stepper/Stepper.styles.js

/**
 * "Back" CTA rendered in place of the stepper on suppressed sub-pages
 * (e.g. Services sub-options, shipping edit, pro-setup). Navigates the user
 * to the previous page via the browser/router history. Matches the Figma
 * design: chevron + bold "Back" text on the far left.
 */
export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 4px 0;
  cursor: pointer;
  font-family: 'Verizon-NHG-eTX', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: #000000;

  .back-chevron {
    display: inline-flex;
    font-size: 16px;
    line-height: 1;
  }

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #EE001E;
    outline-offset: 2px;
    border-radius: 2px;
  }

  @media (max-width: 767px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export const BackCartDivider = styled.div`
  width: 1px;
  height: 40px;
  background: #D8DADA;
  margin: 0 8px;

  @media (min-width: 768px) {
    display: none;
  }
`;

export const SuppressedSpacer = styled.div`
  flex: 1;

  @media (max-width: 767px) {
    display: none;
  }
`;


/* ── Mobile pricing section ──────────────────────────────────────────────── */

/**
 * Container for the pricing summary on mobile (below breadcrumb).
 * Hidden on desktop where pricing is in the CartSummary row.
 */
export const MobilePricingWrapper = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    width: ${({ $isStepperSuppressed }) => ($isStepperSuppressed ? 'auto' : '100%')};
    justify-content: space-between;
    align-items: center;
    padding: 0;
    gap: 8px;
  }
`;

/**
 * Mobile pricing section (left side: Due today, Est monthly, Savings chips).
 */
export const MobilePricingItems = styled.div`
  display: flex;
  gap: 4px;
  flex: ${({ $isStepperSuppressed }) => ($isStepperSuppressed ? '0 1 auto' : '1')};
  align-items: center;
`;

/**
 * Single pricing item on mobile — a rounded chip box (label + value stacked).
 * Matches the Figma design: #F8F7F5 background, 8px border radius.
 *
 * @prop {boolean} isSavings - When true, renders green text for savings.
 */
export const MobilePricingItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #F8F7F5;
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 36px;
  box-sizing: border-box;
`;

/**
 * Mobile pricing label (smaller text at top of chip).
 *
 * @prop {boolean} isSavings - When true, renders in Verizon green (#008331).
 */
export const MobilePricingLabel = styled.span`
  font-family: 'Verizon-NHG-eTX', sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  color: ${({ isSavings }) => (isSavings ? '#008331' : '#000000')};
  text-align: center;
  white-space: nowrap;
`;

/**
 * Mobile pricing value (bold text at bottom of chip).
 *
 * @prop {boolean} isSavings - When true, renders in Verizon green (#008331).
 */
export const MobilePricingValue = styled.span`
  font-family: 'Verizon-NHG-eTX', sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 16px;
  color: ${({ isSavings }) => (isSavings ? '#008331' : '#000000')};
  text-align: center;
  white-space: nowrap;
`;
